import {
  addClass, removeClass, hasClass, reduce, forEach, triggerEvent, translateDefaultCardFormTexts, getBrowserProperties
} from './utils';
import SummaryPage from './SummaryPage';

const selectors = {
  productDetail: '.product__detail'
};

export default class ShoppingBag {
  constructor(id, formProducts, currency, productPages, enableImagePreview) {
    this.id = id;
    this.formProducts = formProducts;
    this.activeProduct = null;
    this.currency = currency;
    this.enableImagePreview = enableImagePreview;
    this.isCameFromEdit = false;

    this.productPages = productPages;

    this.qLine = document.querySelector(`#id_${this.id}`);
    this.paymentCard = this.qLine.querySelector('.jfCard');
    this.footer = this.qLine.querySelector('.paymentCard-footer-wrapper');
    this.header = this.qLine.querySelector('.js-header-wrapper');

    this.productModuleWrapper = this.qLine.querySelector('.jfProductModule-wrapper');
    this.productListWrapper = this.qLine.querySelector('.products');
    this.cardQuestionWrapper = this.qLine.querySelector('.jfCard-question');

    this.products = this.qLine.querySelectorAll('.js-productItem');

    this.shoppingBagButton = this.qLine.querySelector('.shopping-cart');
    this.detailFooterButton = this.qLine.querySelector('.js-detail-footer-button');

    this.hasRequiredProduct = !!this.qLine.querySelector('.product--required');

    this.handleProductClick = this.handleProductClick.bind(this);
    this.detailsBackButtonHandler = this.detailsBackButtonHandler.bind(this);
    this.openSummaryPage = this.openSummaryPage.bind(this);
    this.addToBag = this.addToBag.bind(this);
    this.removeFromBag = this.removeFromBag.bind(this);
    this.handleDetailsRemoveButton = this.handleDetailsRemoveButton.bind(this);
    this.orientationChange = this.orientationChange.bind(this);
    this.onPageChange = this.onPageChange.bind(this);
    this.recalculateProductHeights = this.recalculateProductHeights.bind(this);

    if (this.shoppingBagButton) this.summaryPage = new SummaryPage(this.qLine, this);

    if (this.productPages) this.productPages.setOnPageChange(this.onPageChange);

    // styles are applied late in below browsers, thus extra calculation needed onload
    if (['Firefox', 'Safari'].indexOf(getBrowserProperties().browserName) > -1) {
      window.addEventListener('load', () => {
        this.recalculateProductHeights();
      });
    }
  }

  initShoppingBag() {
    forEach(this.products, p => {
      const pid = p.getAttribute('data-pid');
      // if there is a required product in a product list where single selection is enabled, prevent clicking to unrequired products.
      const isProductRequired = hasClass(p, 'product--required');
      const isSingleSelection = !!p.querySelector('input[type="radio"]');
      const unclickableProduct = this.hasRequiredProduct && isSingleSelection && !isProductRequired;
      if (!unclickableProduct) {
        p.addEventListener('click', () => this.handleProductClick(p));
      }
      p.addEventListener('keypress', e => { if (e.keyCode === 32) e.target.click(); });
      const backButton = p.querySelector('.js-product-back');
      if (backButton) {
        backButton.addEventListener('click', this.detailsBackButtonHandler);
        backButton.addEventListener('keypress', e => { if (e.keyCode === 32) e.target.click(); });
      }

      const addButton = p.querySelector('.js-add-to-bag');
      if (addButton && !unclickableProduct) addButton.addEventListener('click', this.addToBag);

      const productImage = p.querySelector('.form-product-image-with-options');

      if (productImage && (this.enableImagePreview && (this.enableImagePreview === true || this.enableImagePreview === 'true'))) {
        productImage.addEventListener('click', e => {
          e.stopPropagation();
          global.onProductImageClicked(pid);
        });
      }

      const editButton = p.querySelector('.js-update-bag');
      if (editButton) editButton.addEventListener('click', this.addToBag);

      const removeButton = p.querySelector('.js-remove-from-bag');
      if (removeButton) removeButton.addEventListener('click', event => this.handleDetailsRemoveButton(event, p));

      this.setProductHeight(p);
    });

    if (this.shoppingBagButton) {
      this.shoppingBagButton.addEventListener('keypress', e => { if (e.keyCode === 32) e.target.click(); });
      this.shoppingBagButton.addEventListener('click', this.openSummaryPage);
      this.changeShoppingBagButtonPosition();
    }

    if (this.productListWrapper) this.setHeight(this.productListWrapper); // For the first animation.
    if (this.footer) this.setHeight(this.footer); // For the first animation.
    this.updateCartItems();
  }

  recalculateProductHeights() {
    forEach(this.products, p => { this.setProductHeight(p); });
  }

  openSummaryPage() {
    if (!this.shoppingBagButton.hasClassName('empty')) {
      this.hideQuestionHeader();
      this.hideProductList();
      this.hideFooter();
      this.hideShoppingBagButton();

      this.summaryPage.openSummaryPage();

      const products = this.getProductInputIDs();
      this.summaryPage.makeList(products, this.formProducts);
      translateDefaultCardFormTexts();
    }
  }

  closeSummaryPage() {
    this.summaryPage.closeSummaryPage();
    this.showShoppingBagButton();
    this.showFooter();
    this.showProductList();
    this.showQuestionHeader();
  }

  showShoppingBagButton() {
    this.shoppingBagButton.show();
  }

  hideShoppingBagButton() {
    this.shoppingBagButton.hide();
  }

  openProductDetails(inputId) {
    const productsListItems = this.qLine.querySelectorAll('.js-productItem');

    const productIndex = Array.prototype.findIndex.call(productsListItems, item => item.getAttribute('data-input') === inputId);
    const pageIndex = Math.ceil((productIndex + 1) / this.getProductsPerPage());

    if (this.productPages) this.productPages.handlePageChange(pageIndex);
    const product = this.qLine.querySelector(`.js-productItem[data-input="${inputId}"]`);
    if (this.hasProductOption(product)) product.click();
  }

  getProductsPerPage() {
    return this.productPages.getProductsPerPage();
  }

  inputSelectors() {
    return this.getProductInputIDs().reduce((acc, curr) => {
      if (acc !== '') {
        return `${acc}, [id="${curr}"]`;
      }
      return `${acc}[id="${curr}"]`;
    }, '');
  }

  hideQuestionHeader() {
    addClass(this.paymentCard, 'jfCard--summary');
  }

  showQuestionHeader() {
    removeClass(this.paymentCard, 'jfCard--summary');
  }

  hideProductList() {
    this.slideUp(this.productListWrapper);
  }

  showProductList() {
    this.slideDown(this.productListWrapper);
  }

  hideFooter() {
    this.slideUp(this.footer);
  }

  showFooter() {
    this.slideDown(this.footer);
  }

  showDetailPage() {
    this.setNextButtonState(true);
    this.hideFooter();
    this.hideShoppingBagButton();
    addClass(this.productListWrapper, 'products--selected');
  }

  setDetailPageButtons(product) {
    const addButton = product.querySelector('.js-add-to-bag');
    const editButton = product.querySelector('.js-update-bag');
    const removeButton = product.querySelector('.js-remove-from-bag');

    if (hasClass(product, 'product--selected')) {
      addButton.hide();
      editButton.show();
      removeButton.show();
    } else {
      addButton.show();
      editButton.hide();
      removeButton.hide();
    }
  }

  addToBag(event) {
    event.stopPropagation();
    const input = this.activeProduct.querySelector(this.inputSelectors());
    input.checked = true;
    triggerEvent(input, 'change');

    this.calculateTotal();

    this.highlightProductItem(this.activeProduct);

    this.updateCartItems();
    this.backToProductList('details');
    this.setNextButtonState(false);
  }

  handleDetailsRemoveButton(event, product) {
    event.stopPropagation();
    this.removeFromBag(product);
    this.backToProductList('details');
  }

  removeFromBag(product) {
    const input = product.querySelector(this.inputSelectors());
    if (input.disabled || hasClass(product, 'product--required')) return;

    if (this.hasProductOption(product) && hasClass(product, 'just_sold_out_product')) this.resetDetailState(product);

    input.checked = false;
    triggerEvent(input, 'change');

    this.calculateTotal();
    this.removeHighlightProductItem(product, true);
    this.updateCartItems();
  }

  updateCartItems() {
    if (this.shoppingBagButton) {
      const totalItems = this.getTotalSelectedItems();
      this.updateShoppingBagButton(totalItems);
    }
  }

  updateShoppingBagButton(totalItems) {
    if (totalItems > 0) {
      removeClass(this.shoppingBagButton, 'empty');
    } else {
      addClass(this.shoppingBagButton, 'empty');
    }
    this.shoppingBagButton.querySelector('.shopping-cart__count').textContent = totalItems;
  }

  getTotalSelectedItems() {
    const inputSelectors = this.inputSelectors();

    return reduce(this.products, (totalSelectedItems, product) => {
      if (!product.querySelector(inputSelectors).checked) return totalSelectedItems;

      if (!this.hasSubProduct(product)) return totalSelectedItems + 1;

      return totalSelectedItems + reduce(product.querySelectorAll('select[id*="quantity"], input[id*="quantity"]'), (selectedSubProduct, subProduct) => {
        return parseInt(subProduct.value, 10) > 0 ? selectedSubProduct + 1 : selectedSubProduct;
      }, 0);
    }, 0);
  }

  handleProductClick(p) {
    const product = p;

    if (
      !this.hasProductOption(product)
      || hasClass(product, 'just_sold_out_product')
      || hasClass(product, 'sold_out_product')
    ) {
      this.zeroOptionProductClick(product);
    } else {
      if (product === this.activeProduct) return;
      this.showDetailPage(product);

      this.setActiveProduct(product);

      this.setDetailPageButtons(product);
    }

    const inputElement = product.querySelector('input');
    if (inputElement) {
      /* eslint-disable */  
      const changeEventForCondition = new Event('change', { bubbles: true });
      /* eslint-enable */
      inputElement.dispatchEvent(changeEventForCondition);
    }
  }

  backToProductList(current, isEditButtonClicked) {
    if (this.isCameFromEdit === true && current === 'details') {
      removeClass(this.productListWrapper, 'products--selected');
      this.unsetActiveProduct();
      this.openSummaryPage();
      this.isCameFromEdit = false;
    } else if (current === 'summary') {
      this.closeSummaryPage();

      if (isEditButtonClicked) {
        this.isCameFromEdit = true;
      }
    } else if (current === 'details') {
      removeClass(this.productListWrapper, 'products--selected');
      this.unsetActiveProduct();
      this.showShoppingBagButton();
    }

    this.showFooter();
  }

  detailsBackButtonHandler(e) {
    e.stopPropagation();
    const productWrapper = e.target.closest('.form-product-item');
    if (productWrapper.querySelector('.paymentCardDetail-footer-cta--add').style.display !== 'none') {
      if (productWrapper.querySelector('.checkboxProduct-input')) {
        productWrapper.querySelector('.checkboxProduct-input').checked = false;
      } else if (productWrapper.querySelector('.radioProduct-input')) {
        productWrapper.querySelector('.radioProduct-input').checked = false;
      }
      this.resetDetailState(productWrapper);
      this.calculateTotal();
    }
    this.backToProductList('details');
    this.setNextButtonState(false);
  }

  removeHighlightProductItem(product, force = false) {
    const hasOption = this.hasProductOption(product);
    const hasSubProduct = this.hasSubProduct(product);

    if (!hasOption) {
      const button = product.querySelector('.js-add-without-option');
      removeClass(button.parentElement, 'product__action--remove');
    } else if (hasOption && !hasSubProduct) {
      removeClass(product, 'with-edit-option');
    } else if (hasOption && hasSubProduct) {
      const hasSubProductItem = this.hasSelectedSubProductItem(product);
      if (force) removeClass(product, 'product--selected');
      if (hasSubProductItem) return;

      removeClass(product, 'with-edit-option');
    }

    removeClass(product, 'product--selected');
  }

  hasSelectedSubProductItem(product) {
    const quantities = product.querySelectorAll('[id*="quantity"]');
    return reduce(quantities, (acu, curr) => {
      return acu || !(curr.value === 0 || curr.value === '' || curr.value === '0');
    }, false);
  }

  hasSubProduct(product) {
    return hasClass(product, 'js-hasSubProduct');
  }

  hasProductOption(product) {
    return !hasClass(product, 'js-zeroOption');
  }

  highlightProductItem(product) {
    const hasOption = this.hasProductOption(product);

    addClass(product, 'product--selected');

    if (!hasOption) {
      const button = product.querySelector('.js-add-without-option');
      addClass(button.parentElement, 'product__action--remove');
    }
  }

  getProductInputIDs() {
    return Object.keys(global.JotForm.prices).map(p => {
      var splittedKey = p.split('_');
      if (splittedKey.length === 3) {
        return p;
      } if (splittedKey.length === 4 && splittedKey[2] !== 'plan') {
        if (splittedKey[2] !== 'price') { // modified for fetched subs in cardform
          splittedKey.pop(); // pops hash after e.g input_3_xxxxxxxxx
        }
        return splittedKey.join('_');
      }
      return p;
    }).reduce((acc, curr) => {
      if (acc.indexOf(curr) === -1) acc.push(curr);
      return acc;
    }, []);
  }

  zeroOptionProductClick(product) {
    if (hasClass(product, 'sold_out_product')) { return; }
    const input = product.querySelector(this.inputSelectors());

    if (input.checked) {
      this.removeFromBag(product);
    } else {
      input.checked = true;
      if (input.type === 'radio') {
        const allProducts = document.querySelectorAll('.js-productItem');
        allProducts.forEach(pro => {
          this.removeHighlightProductItem(pro);
        });
      }
      this.highlightProductItem(product);
      this.shakeFooter();
      this.calculateTotal();
      this.updateCartItems();
    }
  }

  setActiveProduct(product) {
    this.activeProduct = product;
    addClass(product, 'product--active');
    if (product.hasClassName('product--with-option')) {
      const detail = product.querySelector('.product__detail');
      detail.style = 'display: block;';
    }
    forEach(this.products, p => {
      if (p !== product) {
        this.slideUp(p);
      }
    });
    this.slideDown(product.querySelector(selectors.productDetail));
    this.setHeight(this.productListWrapper);
    window.addEventListener('orientationchange', this.orientationChange);
  }

  unsetActiveProduct() {
    removeClass(this.activeProduct, 'product--active');
    if (this.activeProduct.hasClassName('product--with-option')) {
      const detail = this.activeProduct.querySelector('.product__detail');
      detail.style = 'display: none;';
    }
    forEach(this.products, p => {
      if (p !== this.activeProduct) {
        this.slideDown(p, true);
      }
    });
    this.slideUp(this.activeProduct.querySelector(selectors.productDetail));
    this.setHeight(this.productListWrapper);
    this.activeProduct = null;
    this.activeProductDetailState = null;
    window.removeEventListener('orientationchange', this.orientationChange);
  }

  getProductOptions(product) {
    return product.querySelector(selectors.productDetail) ? product.querySelector(selectors.productDetail).querySelectorAll('select, input') : [];
  }

  resetDetailState(product) {
    const options = this.getProductOptions(product);

    options.forEach(opt => {
      const option = opt;
      if (option.type !== 'hidden' && (option.getAttribute('data-component') === 'quantityOptions' || option.getAttribute('data-type') === 'customQuantity')) {
        if (option.tagName === 'SELECT') {
          option.selectedIndex = 0;
        } else {
          option.value = '0';
        }
      }
      triggerEvent(option, 'change');
    });
  }

  changeShoppingBagButtonPosition() {
    this.cardQuestionWrapper.insertBefore(this.shoppingBagButton, this.productModuleWrapper);
  }

  shakeFooter() {
    if (!this.shoppingBagButton) return;
    // animation class for cart
    addClass(this.shoppingBagButton, 'shopping-cart--added');

    setTimeout(() => {
      removeClass(this.shoppingBagButton, 'shopping-cart--added');
    }, 350);
  }

  calculateTotal() {
    global.JotForm.countTotal(global.JotForm.prices);
  }

  slideDown(ele) {
    const element = ele;

    if (hasClass(element, 'product__detail')) {
      element.parentElement.style.height = 'auto';
    }

    this.setHeight(element);
  }

  slideUp(ele) {
    const element = ele;

    element.style.height = '0px';
  }

  setProductHeight(product) {
    const image = product.querySelector('.product__image img');

    if (image && !image.complete) image.onload = () => this.setHeight(product);

    this.setHeight(product);
  }

  setHeight(ele) {
    const element = ele;
    const clonedElement = element.cloneNode(true);
    clonedElement.style.position = 'absolute';
    clonedElement.style.height = 'auto';
    clonedElement.style.width = '100%';

    if (parseInt(window.getComputedStyle(element).height, 10) === parseInt(window.getComputedStyle(clonedElement).height, 10)) return;

    // DON'T TOUCH THIS
    const radios = clonedElement.querySelectorAll('input[type="radio"]:checked');
    forEach(radios, r => {
      const radio = r;
      radio.checked = false;
    });

    element.parentElement.appendChild(clonedElement);
    const elementHeight = window.getComputedStyle(clonedElement).height;
    element.parentElement.removeChild(clonedElement);

    element.style.height = elementHeight;
  }

  orientationChange() {
    setTimeout(() => {
      this.setHeight(this.activeProduct.querySelector(selectors.productDetail));
      this.setHeight(this.productListWrapper);
    }, 200);
  }

  onPageChange() {
    this.setHeight(this.productListWrapper);
    this.setHeight(this.footer);
  }

  setNextButtonState(isDisabled) {
    if (global.CardForm !== undefined) {
      const getCurrentCard = global.CardForm.getCurrentCard();

      if (getCurrentCard && getCurrentCard.type && getCurrentCard.type === 'products') {
        global.CardForm.getCurrentCard().actionsButtons.next.disabled = isDisabled;
      }
    }
  }
}
